import { holidayApi } from 'Api/masters/holiday';
import ConfirmDelete from 'components/Global/confirmDeleteModel';
import useDebounce from 'components/Global/Hooks/UseDebounce';
import dayjs from 'dayjs';
import { TablePagination, TableSorter } from 'interfaces/global.interface';
import { useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import AddHolidayManuallyModal from './AddHolidayManuallyModal';
import AddHolidayModal from './AddHolidayModal';
import HolidayHeader from './Header';
import HolidayTable from './HolidayTable';

const HolidayComponent = () => {
	const [holidayData, setHolidayData] = useState<any[]>([]);
	const [totalRecords, setTotalRecords] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [sortField, setSortField] = useState('');
	const [sortOrder, setSortOrder] = useState('');
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [deleteHolidayId, setDeleteHolidayId] = useState<string | null>(null);
	const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
	const [isAddHolidayModalOpen, setIsAddHolidayModalOpen] = useState(false);
	const [isAddHolidayManuallyModalOpen, setIsAddHolidayManuallyModalOpen] =
		useState(false);
	const [edit, setEdit] = useState<any>(null);
	const [searchText, setSearchText] = useState('');
	const [date, setDate] = useState<any>(dayjs());
	const debouncedSearchValue = useDebounce(searchText, 500);

	const handleDatePicker = (value: any) => {
		setDate(value);
		setCurrentPage(1);
	};
	const handleSearch = (value: string) => {
		setSearchText(value);
		setCurrentPage(1);
	};
	const handleSearchApi = async (value: string) => {
		//search api
	};

	const tableChangeHandler = (
		pagination: TablePagination,
		filters: any,
		sorter: TableSorter
	) => {
		setTotalRecords(pagination.total);
		setCurrentPage(pagination.current);
		setPageSize(pagination.pageSize);
		let order = '';
		if (sorter?.order === 'ascend') {
			order = 'asc';
		} else if (sorter?.order) {
			order = 'desc';
		}
		setSortOrder(order);
		setSortField(sorter.field);
	};

	const fetchHoliday = async () => {
		try {
			const query = {
				page: currentPage,
				searchText: debouncedSearchValue.trim(),
				sortBy: sortField,
				sortOrder: sortOrder,
				pageSize: pageSize,
				year: dayjs(date).format('YYYY'),
			};
			setIsLoading(true);
			const holidayData = await holidayApi.getAllHolidays(query);

			setHolidayData(holidayData.data.data);
			setTotalRecords(holidayData.data.total);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching holiday';
			toastText(errorMessage, 'error');
		} finally {
			setIsLoading(false);
		}
	};

	const deleteHandler = async () => {
		if (deleteHolidayId) {
			try {
				setIsDeleteLoading(true);
				const response = await holidayApi.deleteHoliday(
					deleteHolidayId
				);
				let message =
					response?.data?.message || 'Holiday deleted successfully';
				toastText(message, 'success');
				cancelDeleteModal();
				fetchHoliday();
			} catch (error: any) {
				toastText(error.response.data.message, 'error');
			} finally {
				setIsDeleteLoading(false);
			}
		}
	};

	const cancelDeleteModal = () => {
		setIsDeleteModalOpen(false);
		setDeleteHolidayId(null);
	};

	const showDeleteModal = () => {
		setIsDeleteModalOpen(true);
	};

	const handleCancel = () => {
		setIsAddHolidayModalOpen(false);
	};

	const showAddHolidayModal = () => {
		setIsAddHolidayModalOpen(true);
	};

	const showAddHolidayManuallyModal = () => {
		setIsAddHolidayManuallyModalOpen(true);
	};

	const closeAddHolidayManuallyModal = () => {
		setIsAddHolidayManuallyModalOpen(false);
		setEdit(null);
	};

	const refreshHoliday = () => {
		setCurrentPage(1);
		fetchHoliday();
	};

	const buttons = [
		{
			text: 'Add Holiday',
			isLoading: false,
			className: 'btn-blue',
			// icon: <AddSvg />,
			isSubmit: true,
			onclick: showAddHolidayModal,
			disabled: isLoading,
		},
	];

	useEffect(() => {
		fetchHoliday();
	}, [
		currentPage,
		date,
		sortField,
		sortOrder,
		pageSize,
		debouncedSearchValue,
	]);
	return (
		<div>
			<div>
				<HolidayHeader
					handleDatePicker={handleDatePicker}
					dateValue={date}
					button={buttons}
					searchText={searchText}
					handleSearch={handleSearch}
					handleSearchApi={handleSearchApi}
					isLoading={isLoading}
					fetchHoliday={refreshHoliday}
				/>
				<HolidayTable
					holidayData={holidayData}
					totalRecords={totalRecords}
					currentPage={currentPage}
					pageSize={pageSize}
					isLoading={isLoading}
					setEditData={setEdit}
					tableChangeHandler={tableChangeHandler}
					showAddHolidayManuallyModal={showAddHolidayManuallyModal}
					showDeleteModal={showDeleteModal}
					setDeleteHolidayId={setDeleteHolidayId}
				/>

				<AddHolidayModal
					isAddHolidayModalOpen={isAddHolidayModalOpen}
					handleCancel={handleCancel}
					fetchHolidays={fetchHoliday}
					showAddHolidayManuallyModal={showAddHolidayManuallyModal}
				/>

				<AddHolidayManuallyModal
					isAddHolidayManuallyModalOpen={
						isAddHolidayManuallyModalOpen
					}
					handleCancel={closeAddHolidayManuallyModal}
					edit={edit}
					fetchHoliday={fetchHoliday}
				/>

				<ConfirmDelete
					isModalOpen={isDeleteModalOpen}
					handleCancel={cancelDeleteModal}
					deleteHandler={deleteHandler}
					isLoading={isDeleteLoading}
					handleOk={cancelDeleteModal}
				/>
			</div>
		</div>
	);
};

export default HolidayComponent;
